import { DefaultUnitTypeId, useDescribeHooks } from '../../../../../features/data-types'
import { PreviewBodyItem } from '../../../../../services/server'
import { TableName } from '../../../../../tables'
import { usePriceOutlineHooks, usePricesHooks } from '../../../../operations'
import { HoardOrder, HoardType, InvoiceableItem, InvoiceMode } from '../../../../sales'

export type HoardInvoicePreview = {
    // Ítems del comprobante: solo los seleccionados para facturar.
    items: PreviewBodyItem[]
    // Monto en espacio de valor (con IVA, sin recargo del medio de pago), comparable con Utilizado/Restante.
    creditAmount: number
    // Total del comprobante a emitir (incluye el recargo del medio de pago).
    documentTotal: number
}

export const useHoardInvoicePreview = () => {
    const { getPriceOutlineLive, getPriceOutlineLiveFromHoardValue } = usePriceOutlineHooks()
    const pricesHooks = usePricesHooks()
    const { describe } = useDescribeHooks()

    const buildHoardInvoicePreview = (
        table: TableName,
        hoard: HoardOrder,
        mode: InvoiceMode,
        invoiceableItems: InvoiceableItem[],
        remainingValue: number
    ): HoardInvoicePreview => {
        const paymentTypeYield = hoard.paymentTypeYield || 0

        // El total restante de un acopio financiero incluye crédito no consumido, que no corresponde a ningún ítem.
        if (hoard.type === HoardType.FINANCIAL && mode === InvoiceMode.TOTAL) {
            const outline = getPriceOutlineLiveFromHoardValue(remainingValue, paymentTypeYield, hoard.vatTaxYield)
            return {
                items: [{
                    name: describe(table, hoard),
                    unitTypeId: DefaultUnitTypeId,
                    unitSubtotal: outline.totalWithoutVAT,
                    unitTotal: outline.total,
                    quantity: 1,
                    totalWithoutVAT: outline.totalWithoutVAT,
                    vatAmount: outline.vatAmount
                }],
                creditAmount: remainingValue,
                documentTotal: outline.total
            }
        }

        // En un acopio financiero el precio de lista ya trae IVA: se descompone en lugar de aplicarlo encima.
        if (hoard.type === HoardType.FINANCIAL) {
            const paymentTypeFactor = 1 + paymentTypeYield / 100
            const items = invoiceableItems.map(({ item, quantity }) => {
                const vatTaxYield = item.vatTaxYield ?? pricesHooks.product.vatTaxYield(item.productId)
                const price = hoard.pricesList?.[item.productId] ?? item.price
                const unitSubtotal = price / (1 + vatTaxYield / 100) * paymentTypeFactor
                const unitTotal = unitSubtotal * (1 + vatTaxYield / 100)
                return {
                    productId: item.productId,
                    quantity,
                    unitSubtotal,
                    unitTotal,
                    totalWithoutVAT: unitSubtotal * quantity,
                    vatAmount: (unitTotal - unitSubtotal) * quantity
                }
            })
            return {
                items,
                creditAmount: invoiceableItems.reduce((sum, { item, quantity }) =>
                    sum + (hoard.pricesList?.[item.productId] ?? item.price) * quantity, 0),
                documentTotal: items.reduce((sum, item) => sum + item.unitTotal * item.quantity, 0)
            }
        }

        const outline = getPriceOutlineLive(
            invoiceableItems.map(({ item, quantity }) => ({ ...item, quantity })),
            undefined,
            paymentTypeYield
        )
        return {
            items: outline.items.map(item => ({
                productId: item.productId,
                quantity: item.quantity,
                unitSubtotal: item.unitSubtotal,
                unitTotal: item.unitTotal,
                totalWithoutVAT: item.totalWithoutVAT,
                vatAmount: item.vatAmount
            })),
            creditAmount: outline.total,
            documentTotal: outline.total
        }
    }

    return {
        buildHoardInvoicePreview
    }
}
